import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, Cell,
} from "recharts";
import { Target, TrendingUp, CheckCircle2, FolderKanban } from "lucide-react";

export default function Productivity() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/productivity/me").then((r) => setData(r.data)).catch(() => {}); }, []);

  if (!data) return <div className="space-y-4"><Skeleton className="h-10 w-56" /><Skeleton className="h-72" /></div>;

  const pct = data.target ? Math.min(100, Math.round((data.today / data.target) * 100)) : 0;
  const chart = data.days.map((d) => ({ date: d.date.slice(5), count: d.count }));
  const cards = [
    { label: "Hari Ini", value: data.today, sub: `dari target ${data.target}`, icon: Target, color: "text-primary" },
    { label: "Target Harian", value: data.target_ml, sub: "mL/hari", icon: TrendingUp, color: "text-red-500" },
    { label: "Total Disetujui", value: data.total, sub: `≈ ${data.total_ml} mL sepanjang waktu`, icon: CheckCircle2, color: "text-emerald-500" },
    { label: "Proyek Aktif", value: data.projects, sub: "ditugaskan", icon: FolderKanban, color: "text-orange-500" },
  ];

  return (
    <div className="space-y-6">
      <div>
        <p className="uppercase text-[10px] tracking-wider font-semibold text-muted-foreground">Produktivitas Pribadi</p>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Produktivitas Saya</h1>
        <p className="text-sm text-muted-foreground mt-1">Arsip yang Anda setujui vs target harian.</p>
      </div>


      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map((c) => (
          <Card key={c.label} className="p-4">
            <div className="flex items-center justify-between">
              <p className="uppercase text-[10px] tracking-wider font-semibold text-muted-foreground">{c.label}</p>
              <c.icon className={`h-4 w-4 ${c.color}`} />
            </div>
            <p className="font-heading text-3xl font-bold mt-2 tabular-nums">{c.value}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{c.sub}</p>
          </Card>
        ))}
      </div>

      <Card className="p-5">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-heading font-semibold">Target Hari Ini</h3>
          <span className="text-sm font-semibold tabular-nums">{data.today} / {data.target}</span>
        </div>
        <Progress value={pct} className="h-3" />
        <p className="text-xs text-muted-foreground mt-2">{pct}% tercapai{pct >= 100 ? " — target tercapai! 🎯" : ""}</p>
      </Card>

      <Card className="p-5">
        <h3 className="font-heading font-semibold mb-4">14 Hari Terakhir</h3>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={chart} margin={{ left: -12 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
            <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
            <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))", fontSize: 12 }} />
            <ReferenceLine y={data.target} stroke="hsl(var(--chart-3))" strokeDasharray="5 4" label={{ value: "Target", fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
            <Bar dataKey="count" radius={[4, 4, 0, 0]}>
              {chart.map((e, i) => <Cell key={i} fill={e.count >= data.target ? "hsl(var(--chart-2))" : "hsl(var(--chart-1))"} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
}
